import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { RxUpdate } from 'react-icons/rx';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import {
  ASSOCIATE_FORM_COUNT_CREATE,
  ASSOCIATE_FORM_COUNT_UPDATE,
  SELECT_FIELD_DEFAULT_VALUE,
  USERNAME_REGEX,
} from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useServiceActions from '../../../hooks/APIHooks/appointments/useServiceActions';
import useAssociateActions from '../../../hooks/APIHooks/users/useAssociateActions';
import useCheckActions from '../../../hooks/APIHooks/users/useCheckActions';
import useCitiesActions from '../../../hooks/APIHooks/users/useCitiesActions';
import useLanguageActions from '../../../hooks/APIHooks/users/useLanguageActions';
import usePostalCodesActions from '../../../hooks/APIHooks/users/usePostalCodesActions';
import useProvincesActions from '../../../hooks/APIHooks/users/useProvincesActions';
import useRolePermissionsActions from '../../../hooks/APIHooks/users/useRolePermissionsActions';
import useRolesActions from '../../../hooks/APIHooks/users/useRolesActions';
import { createEditUserSchema } from '../../../schemas';
import { Associate, AssociateReqBody } from '../../../types/associates';
import { SelectData } from '../../../types/common';
import { Feature } from '../../../types/features';
import { Language } from '../../../types/languages';
import { Permissions } from '../../../types/permissions';
import { User, UserProfile } from '../../../types/users';
import { showPicture } from '../../../utils/commonFunctions';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import { convertImageToBase64 } from '../../../utils/imageUtils';
import RolePermissionTable from '../../SetupSubPageComps/RolePermissionsComps/RolePermissionTable';
import InputFieldBadge from '../Badges/InputFieldBadge';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import IndicationBadge from '../IndicationBadge';
import AcademiaCheckboxField from '../Inputs/AcademiaCheckboxField';
import AcademiaCountrySelect from '../Inputs/AcademiaCountrySelect';
import AcademiaDatePicker from '../Inputs/AcademiaDatePicker';
import AcademiaFileInputField from '../Inputs/AcademiaFileInputField';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaInputField from '../Inputs/AcademiaInputField';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import AcademiaModalLoading from '../Loadings/AcademiaModalLoading';
import BadgeLoading from '../Loadings/BadgeLoading';
import PasswordRestrictionRules from '../PasswordRestrictionRules';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const CreateEditAssociateModal = ({
  title,
  handleModalClose,
  associate,
  associateProfile,
  isProfileLoading,
  featureList,
  isRequestedFromUser,
}: {
  title: string;
  handleModalClose: () => void;
  associate?: Associate | User;
  associateProfile?: UserProfile;
  isProfileLoading?: boolean;
  featureList?: Array<Feature>;
  isRequestedFromUser?: boolean;
}) => {
  // integration of react hooks here
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [countryCode, setCountryCode] = useState<string>('');
  const [country, setCountry] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [username, setUsername] = useState<string>('');

  const [initialPermissionValues, setInitialPermissionValues] = useState<{
    [key: string]: any;
  }>({});
  const [defaultPermissionValues, setDefaultPermissionValues] = useState<{
    [key: string]: any;
  }>({});

  const [formIndex, setFormIndex] = useState<number>(0);

  const [hasCountryReset, setHasCountryReset] = useState<boolean>(false);
  const [hasProvinceReset, setHasProvinceReset] = useState<boolean>(false);
  const [hasCityReset, setHasCityReset] = useState<boolean>(false);
  const [resetLangField, setResetLangField] = useState<boolean>(false);
  const [resetExpertiseField, setResetExpertiseField] =
    useState<boolean>(false);

  const [formikProps, setFormikProps] = useState<FormikProps<any> | null>(null);

  const [selectedRole, setSelectedRole] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [selectedProvince, setSelectedProvince] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [selectedCity, setSelectedCity] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [selectedLang, setSelectedLang] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );
  const [selectedService, setSelectedService] = useState<SelectData>(
    SELECT_FIELD_DEFAULT_VALUE
  );

  const [selectedLanguages, setSelectedLanguages] = useState<Array<Language>>(
    []
  );
  const [selectedServices, setSelectedServices] = useState<Array<SelectData>>(
    []
  );

  const modalRef = useRef<HTMLDivElement>(null);

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // integration of custom API hooks here
  const { createNewAssociateHandler, updateAssociateHandler } =
    useAssociateActions('');

  const { getCheckAndSuggestUsername } = useCheckActions(
    title.toLowerCase().includes('create') ? username : ''
  );

  const { getRoleListHandler } = useRolesActions('roleList');
  const { getCountryWiseAddedProvincesHandler } = useProvincesActions(
    '',
    undefined,
    country.code
  );
  const { getProvinceWiseCitiesHandler } = useCitiesActions(
    '',
    selectedProvince.id !== -1 ? selectedProvince.id : undefined
  );
  const { getPostalCodesWithCityIdHandler } = usePostalCodesActions(
    '',
    selectedCity.id !== -1 ? selectedCity.id : undefined
  );
  const { getAddedLanguagesHandler } = useLanguageActions('addedLanguages');
  const { getServiceListHandler } = useServiceActions('serviceList');
  const { getRolePermissionsByRoleIdHandler } = useRolePermissionsActions(
    '',
    selectedRole.id !== -1 ? selectedRole.id : undefined
  );

  console.log(
    'USERNAME RES: ',
    hasCountryReset,
    hasProvinceReset,
    getCheckAndSuggestUsername.data
  );

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  // setting associate's user name while editing here
  useMemo(() => {
    if (associate) {
      setUsername(associate.username);
    }
  }, [associate]);

  // setting associate profile's default values while editing here
  useEffect(() => {
    if (associateProfile) {
      setSelectedLanguages(associateProfile.languages_spoken);

      setSelectedServices(
        associateProfile.user_expertise.map((expertise) => ({
          id: expertise.service_id,
          name: expertise.service_name,
          code: expertise.id.toString(),
        }))
      );

      setSelectedProvince({
        id: associateProfile.province,
        name: associateProfile.province_name,
      });

      setSelectedCity({
        id: associateProfile.city,
        name: associateProfile.city_name,
      });
    }
  }, [associateProfile]);

  console.log('SELECTED PROVINCE: ', selectedProvince, associateProfile);
  console.log('POSTAL CODES: ', getPostalCodesWithCityIdHandler.data?.data);

  // checking if country has been reselected or not here
  useEffect(() => {
    if (associate) {
      if (country.id !== -1 && associate.country_name !== country.name) {
        setHasCountryReset(true);
        setSelectedProvince(SELECT_FIELD_DEFAULT_VALUE);
        setSelectedCity(SELECT_FIELD_DEFAULT_VALUE);
      }
    } else {
      if (country) {
        setHasCountryReset(true);
        setSelectedProvince(SELECT_FIELD_DEFAULT_VALUE);
        setSelectedCity(SELECT_FIELD_DEFAULT_VALUE);
      }
    }
  }, [country, associate]);

  // setting selected language into the array here
  useEffect(() => {
    if (selectedLang.id !== -1) {
      setSelectedLanguages((state) => [
        ...state,
        {
          id: selectedLang.id,
          name: selectedLang.name,
          code: selectedLang.code as string,
        },
      ]);

      setSelectedLang(SELECT_FIELD_DEFAULT_VALUE);
    }
  }, [selectedLang]);

  // setting selected language into the array here
  useEffect(() => {
    if (selectedService.id !== -1) {
      setSelectedServices((state) => [
        ...state,
        {
          id: selectedService.id,
          name: selectedService.name,
        },
      ]);

      setSelectedService(SELECT_FIELD_DEFAULT_VALUE);
    }
  }, [selectedService]);

  // setting permission boolean fields into initial values here
  useEffect(() => {
    if (featureList?.length) {
      let permissionStates = {};

      featureList.forEach((feature) => {
        permissionStates = {
          ...permissionStates,
          [`${feature.name}_create`]: false,
          [`${feature.name}_read`]: false,
          [`${feature.name}_update`]: false,
          [`${feature.name}_delete`]: false,
        };
      });

      setInitialPermissionValues(permissionStates);
    }
  }, [featureList]);

  // setting permission values after fetching from API here
  useEffect(() => {
    if (formikProps && selectedRole.id !== -1) {
      console.log(formikProps);
      if (
        getRolePermissionsByRoleIdHandler.data?.is_success &&
        featureList?.length
      ) {
        const roleWisePermissions =
          getRolePermissionsByRoleIdHandler.data.data.role_permissions;

        featureList.forEach((feature) => {
          const featurePermissions =
            roleWisePermissions.find(
              (permission) => permission.feature === feature.name
            )?.permission_names || [];

          formikProps.setFieldValue(
            `${feature.name}_create`,
            featurePermissions.includes('CREATE')
          );

          setDefaultPermissionValues((state) => ({
            ...state,
            [`${feature.name}_create`]: featurePermissions.includes('CREATE'),
          }));

          formikProps.setFieldValue(
            `${feature.name}_read`,
            featurePermissions.includes('READ')
          );

          setDefaultPermissionValues((state) => ({
            ...state,
            [`${feature.name}_read`]: featurePermissions.includes('READ'),
          }));

          formikProps.setFieldValue(
            `${feature.name}_update`,
            featurePermissions.includes('UPDATE')
          );

          setDefaultPermissionValues((state) => ({
            ...state,
            [`${feature.name}_update`]: featurePermissions.includes('UPDATE'),
          }));

          formikProps.setFieldValue(
            `${feature.name}_delete`,
            featurePermissions.includes('DELETE')
          );

          setDefaultPermissionValues((state) => ({
            ...state,
            [`${feature.name}_delete`]: featurePermissions.includes('DELETE'),
          }));
        });
      }
    }
  }, [
    featureList,
    getRolePermissionsByRoleIdHandler.data,
    selectedRole.id,
    formikProps,
  ]);

  console.log('INITIAL VALUES: ', initialPermissionValues);

  // handler function to handle create/edit associate feature
  const createEditAssociateHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      console.log('ASSOCIATE REQ VALUES: ', values);

      let profilePictureBase64 = '';

      // converting profile image to Base64 here
      if (values.profile_picture) {
        profilePictureBase64 = (await convertImageToBase64(
          values.profile_picture
        )) as string;
      }

      let body: AssociateReqBody = {
        user: {
          first_name: values.first_name,
          middle_name: values.middle_name,
          last_name: values.last_name,
          email: values.email,
          role: values.role.id,
        },
        user_profile: {
          city: values.city.id,
          postal_code: values.postal_code.name,
          linkedin_profile: values.linkedin_profile,
          languages_spoken: selectedLanguages.map((lang) => ({
            code: lang.code,
            name: lang.name,
          })),
          address: values.address,
          phone: values.phone,
          date_of_birth: values.date_of_birth
            ? moment(values.date_of_birth, 'DD-MM-YYYY').format('YYYY-MM-DD')
            : null,
          license_number: values.license_number,
          business_reg_no: values.business_reg_no,
          hst_no: values.hst_number,
          years_of_experience: values.years_of_experience,
          per_hour_rate: values.per_hour_rate,
          bio: values.bio,
          is_permanent_resident: values.is_permanent,
          profile_picture: profilePictureBase64 as string,
        },
        feature_permissions: null,
      };

      if (!associate) {
        // extracting extra permissions from default permissions here
        const selectedExtraPermissions: Record<string, Array<Permissions>> = {};

        Object.entries(values).forEach(([key, value]) => {
          if (
            (key.includes('_create') ||
              key.includes('_update') ||
              key.includes('_read') ||
              key.includes('_delete')) &&
            value &&
            !defaultPermissionValues[key]
          ) {
            const [featureName, permission] = key.split('_');

            if (!selectedExtraPermissions[featureName]) {
              selectedExtraPermissions[featureName] = [];
            }

            selectedExtraPermissions[featureName].push(
              permission.toUpperCase() as Permissions
            );
          }
        });

        const selectedExtraPermissionsArr = Object.entries(
          selectedExtraPermissions
        ).map(([feature, permission_names]) => ({
          feature,
          permission_names,
        }));

        body = {
          user: {
            ...body.user,
            username: values.username,
            password: values.password,
            created_by: currentUser?.username,
          },
          user_profile: {
            ...body.user_profile,
            created_by: currentUser?.username,
          },
          user_expertise: selectedServices.map((service) => ({
            service_id: service.id,
            created_by: currentUser?.username,
          })),
          feature_permissions: selectedExtraPermissionsArr.length
            ? {
                created_by: currentUser?.username as string,
                feature_permission_data: selectedExtraPermissionsArr,
              }
            : null,
        };

        console.log('CREATE USER BODY: ', body);

        await createNewAssociateHandler
          .mutateAsync({ body })
          .then((res) => {
            if (res.is_success) {
              toast.success('User Created!!');

              handleModalClose();
              queryClient.invalidateQueries({ queryKey: ['associate-list'] });
            } else {
              toast.error(res.message || 'User Creation Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      } else {
        body = {
          ...body,
          user: {
            ...body.user,
            updated_by: currentUser?.username,
          },
          user_profile: {
            ...body.user_profile,
            updated_by: currentUser?.username,
          },
          user_expertise: selectedServices.map((service) =>
            service.code
              ? {
                  id: +service.code,
                  user: associate.id,
                  service_id: service.id,
                  updated_by: currentUser?.username,
                }
              : {
                  user: associate.id,
                  service_id: service.id,
                  created_by: currentUser?.username,
                }
          ),
        };

        console.log('UPDATE USER BODY: ', body);

        await updateAssociateHandler
          .mutateAsync({ associateId: associate.id, body })
          .then((res) => {
            if (res.is_success) {
              toast.success('User Updated!!');

              handleModalClose();

              if (title.includes('profile')) {
                queryClient.invalidateQueries({
                  queryKey: ['current-user-details', currentUser?.id],
                });
                queryClient.invalidateQueries({
                  queryKey: [
                    'user-profile-by-id',
                    (currentUser as User).user_profile_id,
                  ],
                });
              } else {
                queryClient.invalidateQueries({ queryKey: ['associate-list'] });
              }
            } else {
              toast.error(res.message || 'User Updating Failed!!');
            }
          })
          .catch((error) => toast.error(fetchErrorMessage(error)));
      }
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // handler function to remove selected language from array declared here
  const removeSelectedLangHandler = (langCode: string) => {
    setSelectedLanguages((state) =>
      state.filter((lang) => lang.code !== langCode)
    );
  };

  // handler function to remove selected language from array declared here
  const removeSelectedServiceHandler = (serviceId: number) => {
    setSelectedServices((state) =>
      state.filter((service) => service.id !== serviceId)
    );
  };

  // handler function to handle next button click declared here
  const nextBtnClickHandler = async (props: FormikProps<any>) => {
    let fieldsToValidate: Array<string> = [];

    if (formIndex === 0) {
      fieldsToValidate = [
        'username',
        'first_name',
        'last_name',
        'email',
        'role',
        'password',
        'confirm_password',
      ];
    } else if (formIndex === 1) {
      fieldsToValidate = [
        'phone',
        'province',
        'city',
        'postal_code',
        'license_number',
        'years_of_experience',
        'per_hour_rate',
        'address',
      ];
    }

    props.setTouched(
      fieldsToValidate.reduce((acc, field) => ({ ...acc, [field]: true }), {})
    );

    await props.validateForm();

    let errors = fieldsToValidate.reduce((acc, field) => {
      if (props.errors[field]) {
        acc[field] = props.errors[field];
      }
      return acc;
    }, {} as Record<string, any>);

    console.log('VALIDATION ERRORS: ', errors);

    if (Object.keys(errors).length > 0) {
      modalRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }

    setFormIndex((state) => state + 1);
  };

  // rendering create/edit modal component here
  return (
    <ParentModal
      ref={modalRef}
      onClick={handleModalClose}
      customWidthClass='md:w-[95%] lg:w-[900px]'
    >
      <ModalTitle titleText={title} />

      {isProfileLoading ? (
        <AcademiaModalLoading />
      ) : (
        <Formik
          initialValues={{
            is_editing: associate ? true : false,
            update_permissions: false,
            username: '',
            first_name: associate?.first_name || '',
            middle_name: associate?.middle_name || '',
            last_name: associate?.last_name || '',
            email: associate?.email || '',
            role: associate
              ? { id: associate.role_id, name: associate.role_name }
              : SELECT_FIELD_DEFAULT_VALUE,
            password: '',
            confirm_password: '',
            country: associate?.country_name || '',
            province: associateProfile
              ? {
                  id: associateProfile.province,
                  name: associateProfile.province_name,
                }
              : SELECT_FIELD_DEFAULT_VALUE,
            city: associateProfile
              ? { id: associateProfile.city, name: associateProfile.city_name }
              : SELECT_FIELD_DEFAULT_VALUE,
            postal_code: associateProfile
              ? { id: 0, name: associateProfile.postal_code }
              : SELECT_FIELD_DEFAULT_VALUE,
            linkedin_profile: associateProfile?.linkedin_profile || '',
            address: associate?.address || '',
            is_permanent: associateProfile?.is_permanent_resident || false,
            isd_code: associateProfile?.isd_code || '',
            phone: associateProfile?.phone || '',
            date_of_birth: associateProfile?.date_of_birth
              ? moment(associateProfile.date_of_birth).format('DD-MM-YYYY')
              : '',
            license_number: associateProfile?.license_number || '',
            business_reg_no: associateProfile?.business_reg_no || '',
            hst_number: associateProfile?.hst_no || '',
            years_of_experience: associateProfile?.years_of_experience || '',
            per_hour_rate: associateProfile?.per_hour_rate || '',
            bio: associateProfile?.bio || '',
            profile_picture: '',
            ...initialPermissionValues,
          }}
          validationSchema={createEditUserSchema}
          onSubmit={createEditAssociateHandler}
          enableReinitialize
        >
          {(props) => (
            <Form encType='multipart/form-data' className='overflow-x-hidden'>
              <motion.div
                initial={{ x: 0 }}
                animate={{
                  x: `-${(100 / ASSOCIATE_FORM_COUNT_CREATE) * formIndex}%`,
                }}
                transition={{ duration: 0.5 }}
                className='w-[300%] grid grid-cols-3 gap-1'
              >
                <motion.div
                  layout
                  style={{ overflow: 'hidden' }}
                  initial={{ scale: 1, display: 'block' }}
                  animate={
                    formIndex === 0
                      ? { scale: 1, display: 'block' }
                      : { scale: 0 }
                  }
                  transition={{ duration: 0.5 }}
                  key={`form-0`}
                >
                  <h3 className='text-2xl font-light text-base-orange border-b-2 border-dashed border-base-orange mb-3 italic'>
                    Personal Information
                  </h3>

                  <motion.div
                    layout
                    initial={{ height: 'fit-content', display: 'grid' }}
                    animate={
                      formIndex === 0
                        ? { height: 'fit-content', display: 'grid' }
                        : { height: 0, transitionEnd: { display: 'none' } }
                    }
                    transition={{ duration: 0.5 }}
                    className='grid grid-cols-1 md:grid-cols-2 gap-5'
                  >
                    <div className='space-y-5'>
                      {!associate ? (
                        <div>
                          <AcademiaInputField
                            type='text'
                            name='username'
                            label='Username'
                            placeholder='Enter username here...'
                            value={username}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              setUsername(e.target.value);
                              props.setFieldValue('username', e.target.value);

                              if (USERNAME_REGEX.test(e.target.value)) {
                                queryClient.invalidateQueries({
                                  queryKey: [
                                    'check-and-suggest-username',
                                    e.target.value,
                                  ],
                                });
                              }
                            }}
                            isRequired
                            badge={
                              getCheckAndSuggestUsername.isLoading ? (
                                <BadgeLoading />
                              ) : getCheckAndSuggestUsername.data &&
                                props.values.username ? (
                                <InputFieldBadge
                                  badgeText=''
                                  badgeValue={
                                    getCheckAndSuggestUsername.data.message
                                  }
                                  isValid={
                                    getCheckAndSuggestUsername.data.message ===
                                    'Available'
                                  }
                                />
                              ) : null
                            }
                          />

                          <AnimatePresence
                            initial={false}
                            mode='wait'
                            onExitComplete={() => null}
                          >
                            {getCheckAndSuggestUsername.data?.data ? (
                              <motion.label
                                initial={{
                                  y: -20,
                                  opacity: 0,
                                  display: 'none',
                                }}
                                animate={
                                  getCheckAndSuggestUsername.data.message ===
                                  'Already Taken'
                                    ? { y: 0, opacity: 1, display: 'block' }
                                    : {
                                        y: -20,
                                        opacity: 0,
                                        transitionEnd: { display: 'none' },
                                      }
                                }
                                htmlFor={`input-user`}
                                className='block mt-1 text-base-red text-sm font-light'
                              >
                                Suggested:
                                {getCheckAndSuggestUsername?.data?.data.map(
                                  (suggestion, index) => (
                                    <button
                                      key={index}
                                      type='button'
                                      onClick={() => {
                                        setUsername(suggestion);
                                        props.setFieldValue(
                                          'username',
                                          suggestion
                                        );
                                      }}
                                      className='font-semibold text-base-cyan  hover:opacity-70 duration-300 mx-1'
                                    >
                                      <span className='underline'>
                                        {suggestion}
                                      </span>
                                      {index <
                                      (getCheckAndSuggestUsername?.data?.data
                                        ?.length as number) -
                                        1
                                        ? ', '
                                        : ''}
                                    </button>
                                  )
                                )}
                                etc.
                              </motion.label>
                            ) : null}
                          </AnimatePresence>
                        </div>
                      ) : null}

                      <AcademiaInputField
                        type='text'
                        name='first_name'
                        label='First Name'
                        placeholder='Enter first name here...'
                        isRequired
                      />

                      <AcademiaInputField
                        type='text'
                        name='middle_name'
                        label='Middle Name'
                        placeholder='Enter middle name here...'
                      />

                      <AcademiaInputField
                        type='text'
                        name='last_name'
                        label='Last Name'
                        placeholder='Enter last name here...'
                        isRequired
                      />

                      {!associate && (
                        <AcademiaInputField
                          type='email'
                          name='email'
                          label='Email'
                          placeholder='Enter email here...'
                          isRequired
                        />
                      )}
                    </div>

                    <div className='space-y-5'>
                      <AcademiaFormSelect
                        name='role'
                        label='User Role'
                        options={
                          getRoleListHandler.data?.is_success
                            ? getRoleListHandler.data.data.map((role) => ({
                                id: role.id,
                                name: role.role_name,
                              }))
                            : []
                        }
                        defaultValue={props.values.role}
                        setValueOnChange={(role) => {
                          setSelectedRole(role);
                          setFormikProps(props);
                        }}
                        isRequired
                      />

                      {associate && (
                        <AcademiaInputField
                          type='email'
                          name='email'
                          label='Email'
                          placeholder='Enter email here...'
                          isRequired
                          readOnly={associate}
                        />
                      )}

                      {!isRequestedFromUser && !associate ? (
                        <>
                          <div>
                            <AcademiaInputField
                              type='password'
                              name='password'
                              label='Password'
                              placeholder='Enter password here...'
                              isRequired
                            />

                            <PasswordRestrictionRules
                              password={props.values.password}
                              isTouched={!!props.touched.password}
                              error={props.errors.password as string}
                            />
                          </div>

                          <AcademiaInputField
                            type='password'
                            name='confirm_password'
                            label='Confirm Password'
                            placeholder='Retype password to confirm here...'
                            isRequired
                          />
                        </>
                      ) : null}
                    </div>
                  </motion.div>
                </motion.div>

                <motion.div
                  style={{ overflow: 'hidden' }}
                  initial={{ scale: 0 }}
                  animate={
                    formIndex === 1
                      ? { scale: 1, display: 'block' }
                      : { scale: 0 }
                  }
                  transition={{ duration: 0.5 }}
                  key={`form-1`}
                >
                  <h3 className='text-2xl font-light text-base-orange border-b-2 border-dashed border-base-orange mb-3 italic'>
                    Corporate Information
                  </h3>

                  <motion.div
                    layout
                    initial={{ height: 0, display: 'none' }}
                    animate={
                      formIndex === 1
                        ? { height: 'fit-content', display: 'block' }
                        : { height: 0, transitionEnd: { display: 'none' } }
                    }
                    transition={{ duration: 0.5 }}
                    className='space-y-5'
                  >
                    <div className='grid md:grid-cols-2 gap-5'>
                      <AcademiaCountrySelect
                        name='country'
                        label='Select Country'
                        setCountry={setCountry}
                        defaultCountryCode={associate && associate.iso_code}
                        isRequired
                      />

                      <AcademiaInputField
                        type='number'
                        name='phone'
                        label='Phone Number'
                        placeholder='Enter phone number here...'
                        setCountryCode={setCountryCode}
                        defaultSelectedCountryCode={country.code}
                        isRequired
                      />

                      <AcademiaFormSelect
                        name='province'
                        label='Select Province'
                        options={
                          getCountryWiseAddedProvincesHandler.data?.is_success
                            ? getCountryWiseAddedProvincesHandler.data.data.map(
                                (province) => ({
                                  id: province.id,
                                  name: province.province_name,
                                })
                              )
                            : []
                        }
                        defaultValue={props.values.province}
                        setValueOnChange={(province) => {
                          setSelectedProvince(province);
                          setHasProvinceReset(true);
                        }}
                        resetSelectField={hasCountryReset}
                        setResetSelectField={() => setHasCountryReset(false)}
                        showSearchInput
                        isDisabled={
                          !getCountryWiseAddedProvincesHandler.data?.is_success
                        }
                        isRequired
                      />

                      <AcademiaInputField
                        type='text'
                        name='license_number'
                        label='License Number'
                        placeholder='Enter license number here...'
                        isRequired
                      />

                      <AcademiaFormSelect
                        name='city'
                        label='Select City'
                        options={
                          getProvinceWiseCitiesHandler.data?.is_success
                            ? getProvinceWiseCitiesHandler.data.data.map(
                                (city) => ({
                                  id: city.id,
                                  name: city.city_name,
                                })
                              )
                            : []
                        }
                        defaultValue={props.values.city}
                        setValueOnChange={(city) => {
                          setSelectedCity(city);
                          setHasCityReset(true);
                        }}
                        resetSelectField={hasCountryReset || hasProvinceReset}
                        setResetSelectField={() => {
                          setHasCountryReset(false);
                          setHasProvinceReset(false);
                        }}
                        showSearchInput
                        isDisabled={
                          !getProvinceWiseCitiesHandler.data?.is_success
                        }
                        isRequired
                      />

                      <AcademiaInputField
                        type='text'
                        name='business_reg_no'
                        label='Business Registration Number'
                        placeholder='Enter business registration number here...'
                        isRequired
                      />

                      <AcademiaFormSelect
                        name='postal_code'
                        label='Select Postal Code'
                        options={
                          getPostalCodesWithCityIdHandler.data?.is_success
                            ? getPostalCodesWithCityIdHandler.data.data[0].postal_codes.map(
                                (postalCode, index) => ({
                                  id: index,
                                  name: postalCode,
                                })
                              )
                            : []
                        }
                        defaultValue={props.values.postal_code}
                        resetSelectField={
                          hasCountryReset || hasProvinceReset || hasCityReset
                        }
                        setResetSelectField={() => {
                          setHasCountryReset(false);
                          setHasProvinceReset(false);
                          setHasCityReset(false);
                        }}
                        showSearchInput
                        isDisabled={
                          !getPostalCodesWithCityIdHandler.data?.is_success
                        }
                        isRequired
                      />

                      <AcademiaInputField
                        type='text'
                        name='hst_number'
                        label='HST/TIN Number'
                        placeholder='Enter HST/TIN number here...'
                        isRequired
                      />

                      <AcademiaInputField
                        type='number'
                        name='years_of_experience'
                        label='Years of Experience'
                        placeholder='Enter experience in years here...'
                        isRequired
                      />

                      <AcademiaInputField
                        type='number'
                        name='per_hour_rate'
                        label='Expected Hourly Rate'
                        placeholder='Enter expected rate per hour in CAD here...'
                        isRequired
                      />

                      <div>
                        <AcademiaFormSelect
                          name='spoken_language'
                          label='Select Spoken Languages'
                          options={
                            getAddedLanguagesHandler.data?.is_success
                              ? getAddedLanguagesHandler.data.data.filter(
                                  (lang) =>
                                    !selectedLanguages.some(
                                      (selectedLang) =>
                                        selectedLang.code === lang.code
                                    )
                                )
                              : []
                          }
                          defaultValue={selectedLang}
                          setValueOnChange={(value) => {
                            setSelectedLang(value);
                            setResetLangField(true);
                          }}
                          setResetSelectField={() => setResetLangField(false)}
                          resetSelectField={resetLangField}
                          showSearchInput
                          isRequired
                        />

                        {selectedLanguages.length ? (
                          <div className='flex flex-wrap gap-2 my-4 border-2 border-legal-dark-blue border-dashed rounded-md p-4'>
                            {selectedLanguages.map((selectedLang) => (
                              <SkillBadge
                                key={selectedLang.id}
                                badgeLabel={selectedLang.name}
                                color='#1ABC9C'
                                onClickRemove={() =>
                                  removeSelectedLangHandler(selectedLang.code)
                                }
                                isEditing
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>

                      <div>
                        <AcademiaFormSelect
                          name='field_of_expertise'
                          label='Select Field of Expertise'
                          options={
                            getServiceListHandler.data?.is_success
                              ? getServiceListHandler.data.data
                                  .filter(
                                    (service) =>
                                      !selectedServices.some(
                                        (selectedService) =>
                                          selectedService.id === service.id
                                      )
                                  )
                                  .map((service) => ({
                                    id: service.id,
                                    name: service.name,
                                  }))
                              : []
                          }
                          defaultValue={selectedService}
                          setValueOnChange={(value) => {
                            setSelectedService(value);
                            setResetExpertiseField(true);
                          }}
                          setResetSelectField={() =>
                            setResetExpertiseField(false)
                          }
                          resetSelectField={resetExpertiseField}
                          showSearchInput
                          isRequired
                        />

                        {selectedServices.length ? (
                          <div className='flex flex-wrap gap-2 my-4 border-2 border-legal-dark-blue border-dashed rounded-md p-4'>
                            {selectedServices.map((service) => (
                              <SkillBadge
                                key={service.id}
                                badgeLabel={service.name}
                                color='#1ABC9C'
                                onClickRemove={() =>
                                  removeSelectedServiceHandler(service.id)
                                }
                                isEditing
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>

                      <AcademiaInputField
                        type='text'
                        name='linkedin_profile'
                        label='Linkedin Profile URL'
                        placeholder='Enter linked in profile URL here...'
                      />

                      <AcademiaDatePicker
                        name='date_of_birth'
                        label='Date of Birth'
                        placeholder='DD-MM-YYYY'
                        noInitialDefaultDate
                        maxDate={
                          new Date(
                            new Date().setFullYear(
                              new Date().getFullYear() - 18
                            )
                          )
                        }
                        defaultDate={
                          new Date(
                            new Date().setFullYear(
                              new Date().getFullYear() - 18
                            )
                          )
                        }
                      />

                      <AcademiaTextAreaField
                        name='address'
                        label='Address'
                        placeholder={`Enter user's address here...`}
                        isRequired
                      />

                      <AcademiaTextAreaField
                        name='bio'
                        label='Bio'
                        placeholder={`Enter user's bio here...`}
                      />

                      <div className='!-mt-4 col-span-2'>
                        <AcademiaCheckboxField
                          name='is_permanent'
                          label='Is Permanent Resident?'
                        />
                      </div>
                    </div>

                    <AcademiaFileInputField
                      name='profile_picture'
                      label='Profile Picture'
                      btnText='Upload'
                      accept='images/*'
                      placeholder='Upload profile picture here...'
                      filePath={
                        associateProfile?.profile_picture
                          ? showPicture(associateProfile.profile_picture)
                          : ''
                      }
                    />
                  </motion.div>
                </motion.div>

                <motion.div
                  style={{ overflow: 'hidden' }}
                  initial={{ scale: 0, display: 'none' }}
                  animate={
                    formIndex === 2
                      ? { scale: 1, display: 'block' }
                      : { scale: 0 }
                  }
                  transition={{ duration: 0.5 }}
                  key={`form-2`}
                >
                  <h3 className='text-2xl font-light text-base-orange border-b-2 border-dashed border-base-orange mb-3'>
                    Associate's Permissions
                  </h3>

                  {props.values.role.id !== -1 ? (
                    <>
                      <div className='flex flex-col md:flex-row gap-2 md:items-center justify-between mb-4'>
                        <div className='w-full md:w-fit flex items-center gap-1 text-xl text-legal-dark-blue italic border-2 border-legal-dark-blue border-dashed px-4 py-1 rounded-lg'>
                          <h3 className='font-light'>Selected Role</h3>
                          <h3>:</h3>
                          <h3 className='font-semibold'>
                            {props.values.role.name}
                          </h3>
                        </div>

                        <div className='border-2 border-dashed border-legal-dark-blue px-4 py-1 rounded-md'>
                          <AcademiaCheckboxField
                            name='update_permissions'
                            label='Give User Extra Privilege?'
                          />
                        </div>
                      </div>

                      {featureList?.length && (
                        <>
                          <RolePermissionTable
                            featureList={featureList}
                            defaultPermissionValues={defaultPermissionValues}
                            isReadOnly={!props.values.update_permissions}
                          />

                          <div className='mt-2 flex items-center justify-center md:justify-end gap-3'>
                            <IndicationBadge
                              indicationText='Role Based Permissions'
                              colorHex='#FC7300' //MJA: CHECK THIS HEX #FF8042
                            />
                            <IndicationBadge
                              indicationText='Extra Permissions'
                              colorHex='#35476F'
                            />
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <p className='text-center text-base-red font-semibold text-lg'>
                      Please, select a role first to see permissions!
                    </p>
                  )}
                </motion.div>
              </motion.div>

              <div className='mt-5 space-y-5'>
                <div className='w-full md:w-3/5 mx-auto flex items-center justify-center gap-4'>
                  <AcademiaButton
                    type='button'
                    onClick={() => setFormIndex((state) => state - 1)}
                    variant='info'
                    isDisabled={formIndex === 0}
                    icon={<FiChevronsLeft className='size-6' />}
                    fullWidth
                  >
                    Previous
                  </AcademiaButton>

                  <AcademiaButton
                    title={
                      !associate?.id
                        ? !selectedLanguages.length
                          ? 'Please, select some languages to proceed!'
                          : !selectedServices.length
                          ? 'Please, select some services to proceed!'
                          : undefined
                        : undefined
                    }
                    type='button'
                    onClick={() => nextBtnClickHandler(props)}
                    variant='primary'
                    isDisabled={
                      (formIndex === 1 &&
                        (!selectedLanguages.length ||
                          !selectedServices.length)) ||
                      associate?.id
                        ? formIndex === ASSOCIATE_FORM_COUNT_UPDATE - 1
                        : formIndex === ASSOCIATE_FORM_COUNT_CREATE - 1
                    }
                    icon={<FiChevronsRight className='size-6' />}
                    fullWidth
                    isIconPositionLast
                  >
                    Next
                  </AcademiaButton>
                </div>

                <div className='w-full md:w-3/5 mx-auto flex items-center justify-center gap-4'>
                  {associate?.id &&
                  formIndex === ASSOCIATE_FORM_COUNT_UPDATE - 1 ? (
                    <AcademiaButton
                      title={
                        props.values.role.id === -1
                          ? 'Please, select a role first!'
                          : !associate &&
                            getCheckAndSuggestUsername.data?.message ===
                              'Already Taken'
                          ? 'Please, provide a valid username'
                          : props.values.province.id === -1
                          ? 'Please, select a province first!'
                          : props.values.city.id === -1
                          ? 'Please, select a city first!'
                          : props.values.postal_code.id === -1
                          ? 'Please, select a postal code first!'
                          : !selectedLanguages.length
                          ? 'Please, select some languages first!'
                          : !selectedServices.length
                          ? 'Please, select some expertise first!'
                          : undefined
                      }
                      type='submit'
                      variant='premium'
                      isLoading={props.isSubmitting}
                      isDisabled={
                        props.values.role.id === -1 ||
                        (!associate &&
                          getCheckAndSuggestUsername.data?.message ===
                            'Already Taken') ||
                        props.values.province.id === -1 ||
                        props.values.city.id === -1 ||
                        props.values.postal_code.id === -1 ||
                        !selectedLanguages.length ||
                        !selectedServices.length
                      }
                      icon={<RxUpdate className='size-6' />}
                      fullWidth
                    >
                      update Associate
                    </AcademiaButton>
                  ) : (
                    formIndex === ASSOCIATE_FORM_COUNT_CREATE - 1 && (
                      <AcademiaButton
                        title={
                          props.values.role.id === -1
                            ? 'Please, select a role first!'
                            : !associate &&
                              getCheckAndSuggestUsername.data?.message ===
                                'Already Taken'
                            ? 'Please, provide a valid username!'
                            : props.values.province.id === -1
                            ? 'Please, select a province first!'
                            : props.values.city.id === -1
                            ? 'Please, select a city first!'
                            : props.values.postal_code.id === -1
                            ? 'Please, select a postal code first!'
                            : !selectedLanguages.length
                            ? 'Please, select some languages first!'
                            : !selectedServices.length
                            ? 'Please, select some expertise first!'
                            : undefined
                        }
                        type='submit'
                        variant='premium'
                        isLoading={props.isSubmitting}
                        isDisabled={
                          props.values.role.id === -1 ||
                          (!associate &&
                            getCheckAndSuggestUsername.data?.message ===
                              'Already Taken') ||
                          props.values.province.id === -1 ||
                          props.values.city.id === -1 ||
                          props.values.postal_code.id === -1 ||
                          !selectedLanguages.length ||
                          !selectedServices.length
                        }
                        icon={<TbSquareRoundedPlusFilled className='size-6' />}
                        fullWidth
                      >
                        create Associate
                      </AcademiaButton>
                    )
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </ParentModal>
  );
};

export default CreateEditAssociateModal;
